import { useEffect, useState } from 'react';

import { useHttp } from '@/shared/api/provider';
import { REGEX_PATTERNS } from '@/shared/config/common/constants/patterns';

import { useBooking } from '../store/booking';

import type { IMakeBooking, IPayloadMakeBooking } from '@/entities/booking/model/types';

interface UseMakeBookingProps {
  doctorId: number | null;
  medcenterId: number | null;
}

interface UseMakeBookingHook {
  makeBookingData: IPayloadMakeBooking;
  isValidBookingData: boolean;
  submitLoading: boolean;

  setInitialMakeBooking: () => Promise<void>;
  setFieldMakeBooking: (field: keyof IPayloadMakeBooking, value: string | number) => void;
  submitBooking: (callback: (response: IMakeBooking) => Promise<void>) => Promise<void>;
  submitNewCode: (callback: (response: IMakeBooking) => void) => Promise<void>;
}

export const useMakeBooking = (props: UseMakeBookingProps): UseMakeBookingHook => {
  const { doctorId, medcenterId } = props;

  const repositories = useHttp();

  const { booking } = useBooking();

  const [makeBookingData, setMakeBookingData] = useState<IPayloadMakeBooking>({} as IPayloadMakeBooking);
  const [isValidBookingData, setIsValidBookingData] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(booking.find(item => item.doctorId === doctorId));

  const setFieldMakeBooking = (field: keyof IPayloadMakeBooking, value: string | number) => {
    setMakeBookingData(prevValue => ({
      ...prevValue,
      [field]: value,
    }));
  };

  const setInitialMakeBooking = async () => {
    setFieldMakeBooking('bookingType', 'offline');
    setFieldMakeBooking('fullName', '');
    setFieldMakeBooking('iin', '');
    setFieldMakeBooking('phoneNumber', '');

    if (currentBooking?.changedSlot) {
      setFieldMakeBooking('preferredTime', `${currentBooking.changedSlot.day.date} ${currentBooking.changedSlot.slot}`);
    } else {
      setFieldMakeBooking('preferredTime', '');
    }
  };

  const submitNewCode = async (callback: (response: IMakeBooking) => void) => {
    const payload = {
      ...makeBookingData,
      phoneNumber: makeBookingData.phoneNumber.replace(/\D/g, ''),
      bookingType: makeBookingData.bookingType || 'offline',
    };

    const response = await repositories.booking.makeBooking(payload);

    callback(response);
  };

  const submitBooking = async (callback: (response: IMakeBooking) => Promise<void>) => {
    if (isValidBookingData) {
      const payload = {
        ...makeBookingData,
        phoneNumber: makeBookingData.phoneNumber.replace(/\D/g, ''),
        bookingType: makeBookingData.bookingType || 'offline',
      };

      setSubmitLoading(true);
      const response = await repositories.booking.makeBooking(payload);
      setSubmitLoading(false);

      await callback(response);
    }
  };

  useEffect(() => {
    if (doctorId) {
      setFieldMakeBooking('doctorId', doctorId);
    }

    if (medcenterId) {
      setFieldMakeBooking('medcenterId', medcenterId);
    }

    if (currentBooking?.changedSlot) {
      setFieldMakeBooking('preferredTime', `${currentBooking.changedSlot.day.date} ${currentBooking.changedSlot.slot}`);
    } else {
      setFieldMakeBooking('preferredTime', '');
    }

    setFieldMakeBooking('bookingType', 'offline');
  }, [doctorId, medcenterId, currentBooking]);

  useEffect(() => {
    const isPhoneNumberValid = (phoneNumber: string): boolean => {
      const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
      return cleanedPhoneNumber.length === 11;
    };

    const isFullNameValid = (fullName: string): boolean => {
      const isValidFormat = !REGEX_PATTERNS.FULL_NAME.test(fullName);

      return fullName.trim().length >= 3 && fullName.trim().split(' ').length > 1 && !isValidFormat;
    };

    const isPreferredTimeValid = (preferredTime: string): boolean => {
      return preferredTime.trim().length >= 3;
    };

    const isIinValid = (iin: string): boolean => {
      return iin.length === 12;
    };

    const isDataValid =
      isFullNameValid(makeBookingData.fullName || '') &&
      isPhoneNumberValid(makeBookingData.phoneNumber || '') &&
      (!currentBooking?.iinRequired || isIinValid(makeBookingData.iin!)) &&
      (Boolean(currentBooking?.changedSlot) || isPreferredTimeValid(makeBookingData.preferredTime));

    setIsValidBookingData(isDataValid);
  }, [makeBookingData, currentBooking]);

  useEffect(() => {
    setCurrentBooking(booking.find(item => item.doctorId === doctorId));
  }, [booking]);

  return {
    makeBookingData,
    isValidBookingData,
    submitLoading,

    setInitialMakeBooking,
    setFieldMakeBooking,

    submitBooking,
    submitNewCode,
  };
};
